import { Box, Divider, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryModel } from 'shared/types/Entries';

import { EntriesIcon } from '@/assets/svg';
import { Button } from '@/components';
import { COLOR } from '@/theme/colors';

import EntriesList from '../EntriesList';
import SelectionOutlinedBox from '../SelectionOutlinedBox';

import SelectEntriesModal from './components';
import { Props } from './types';

function SelectEntriesSection({
    onRemoveEntry,
    onSelectEntries,
    selectedEntries,
    CreateEntryModal,
    buttonsPosition = 'bottom',
    sectionLabel,
    testIdPrefix,
    multiselect = true,
    selectCondition,
    conditionWarning,
    disabled,
    entriesToSelect,
    entriesAllowedHorses,
    financialDetails,
    showTax,
    taxRate,
    defaultTaxRate,
    onTaxRateChange,
    calculatedTax,
    currencySymbol,
    onEntryEdited,
}: Props) {
    const { t } = useTranslation();

    const [isSelectEntriesModalOpen, setIsSelectEntriesModalOpen] =
        useState(false);
    const [isTaxEditMode, setIsTaxEditMode] = useState(false);
    const [taxEditValue, setTaxEditValue] = useState('');
    const [taxError, setTaxError] = useState('');

    const openSelectEntriesModal = useCallback(() => {
        setIsSelectEntriesModalOpen(true);
    }, []);

    const closeSelectEntriesModal = useCallback(() => {
        setIsSelectEntriesModalOpen(false);
    }, []);

    const [isAddEntryModalOpen, setIsAddEntryModalOpen] =
        useState<boolean>(false);

    const openAddContactModal = useCallback(
        () => setIsAddEntryModalOpen(true),
        [setIsAddEntryModalOpen],
    );

    const closeAddContactModal = useCallback(
        () => setIsAddEntryModalOpen(false),
        [setIsAddEntryModalOpen],
    );

    const [entryToEdit, setEntryToEdit] = useState<EntryModel | null>(null);

    const closeEditEntry = useCallback(() => {
        setEntryToEdit(null);
        onEntryEdited?.();
    }, [onEntryEdited]);

    const addCreatedEntryToSelection = useCallback(
        (entry: EntryModel) => onSelectEntries([...selectedEntries, entry]),
        [onSelectEntries, selectedEntries],
    );

    const onItemClick = useCallback(
        (entry: EntryModel) => setEntryToEdit(entry),
        [],
    );

    const handleTaxEdit = useCallback(() => {
        setTaxEditValue(taxRate || '0');
        setTaxError('');
        setIsTaxEditMode(true);
    }, [taxRate]);

    const handleTaxConfirm = useCallback(() => {
        const formatted = taxEditValue.replace(',', '.');
        if (!formatted || isNaN(Number(formatted))) {
            setTaxError(t('Invoice:tax_invalid'));
            return;
        }
        if (Number(formatted) < 0) {
            setTaxError(t('Invoice:tax_negative'));
            return;
        }
        setTaxError('');
        onTaxRateChange?.(formatted);
        setIsTaxEditMode(false);
    }, [taxEditValue, onTaxRateChange, t]);

    const handleTaxCancel = useCallback(() => {
        setIsTaxEditMode(false);
    }, []);

    const isTaxCustomized = taxRate !== defaultTaxRate;

    return (
        <SelectionOutlinedBox
            onSelect={openSelectEntriesModal}
            onAdd={openAddContactModal}
            disabled={disabled}
            title={t('SelectEntrySection:title')}
            addLabel={t('SelectEntrySection:add')}
            selectLabel={t('SelectEntrySection:select')}
            SelectIcon={EntriesIcon}
            buttonsPosition={buttonsPosition}
            sectionLabel={sectionLabel}
            addButtonTestId={`${testIdPrefix}-CreateEntriesButton`}
            selectButtonTestId={`${testIdPrefix}-SelectEntriesButton`}
        >
            {!selectedEntries.length ? (
                <Typography
                    color={COLOR.regentGray}
                    variant="body2"
                    fontSize="small"
                    data-test-id={`${testIdPrefix}-SelectedEntriesEmptyStateText`}
                    sx={{
                        display: 'flex',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    {t('SelectEntrySection:blankslate')}
                </Typography>
            ) : (
                <Box className="flex flex-col w-full">
                    <EntriesList
                        testIdPrefix={testIdPrefix}
                        items={selectedEntries}
                        onRemove={onRemoveEntry}
                        removable={!disabled}
                        editable={!disabled}
                        onEditButtonClick={onItemClick}
                        actionIconColor={COLOR.regentGray}
                        financialDetails={financialDetails}
                        itemWrapperStyle="border border-gray-200 rounded-xl w-full mb-4"
                        size="large"
                    />
                    {showTax && selectedEntries.length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginLeft: '90px',
                                    marginRight: '12px',
                                }}
                            >
                                <Box className="flex flex-col gap-1">
                                    <Box className="flex items-center gap-1">
                                        <Typography
                                            fontWeight="bold"
                                            fontSize={14}
                                        >
                                            {t('InvoiceSystem:sales_tax')}
                                        </Typography>
                                        <Typography fontSize={14}>
                                            {taxRate || '0'}%
                                        </Typography>
                                        <Typography
                                            fontSize={14}
                                            color={
                                                isTaxCustomized
                                                    ? COLOR.darkYellow
                                                    : COLOR.regentGray
                                            }
                                        >
                                            (
                                            {isTaxCustomized
                                                ? 'customized'
                                                : 'default'}
                                            )
                                        </Typography>
                                        {!isTaxEditMode && (
                                            <Typography
                                                fontSize={14}
                                                sx={{
                                                    cursor: 'pointer',
                                                    ml: 0.5,
                                                }}
                                                color={COLOR.deepCerulean}
                                                onClick={handleTaxEdit}
                                            >
                                                {t('Actions:edit')}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isTaxEditMode && (
                                        <Box className="flex items-center gap-2">
                                            <TextField
                                                type="text"
                                                value={taxEditValue}
                                                onChange={(e) => {
                                                    setTaxEditValue(
                                                        e.target.value,
                                                    );
                                                    setTaxError('');
                                                }}
                                                error={!!taxError}
                                                inputProps={{ maxLength: 5 }}
                                                size="small"
                                                sx={{ width: 70 }}
                                                data-test-id={`${testIdPrefix}-EntriesTaxRateInput`}
                                            />
                                            <Button
                                                caption={t('Actions:confirm')}
                                                onClick={handleTaxConfirm}
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    backgroundColor:
                                                        COLOR.deepCerulean,
                                                    color: COLOR.white,
                                                    '&:hover': {
                                                        backgroundColor:
                                                            COLOR.deepCerulean,
                                                        opacity: 0.9,
                                                    },
                                                }}
                                                data-test-id={`${testIdPrefix}-EntriesTaxRateConfirm`}
                                            />
                                            <Typography
                                                sx={{ cursor: 'pointer' }}
                                                color={COLOR.deepCerulean}
                                                onClick={handleTaxCancel}
                                            >
                                                {t('Actions:cancel')}
                                            </Typography>
                                        </Box>
                                    )}
                                    {taxError && (
                                        <Typography color="error" fontSize={12}>
                                            {taxError}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography fontWeight="bold" fontSize={14}>
                                    {currencySymbol}
                                    {(calculatedTax ?? 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            )}
            <SelectEntriesModal
                close={closeSelectEntriesModal}
                isOpen={isSelectEntriesModalOpen}
                onSelectEntries={onSelectEntries}
                selectedEntries={selectedEntries}
                multiSelect={multiselect}
                selectCondition={selectCondition}
                conditionWarning={conditionWarning}
                entriesToSelect={entriesToSelect}
            />
            {isAddEntryModalOpen ? (
                <CreateEntryModal
                    close={closeAddContactModal}
                    onSave={addCreatedEntryToSelection}
                    horsesToSelect={entriesAllowedHorses}
                    closeOnSubmit
                />
            ) : null}
            {entryToEdit ? (
                <CreateEntryModal
                    entry={entryToEdit}
                    isEditMode
                    close={closeEditEntry}
                    horsesToSelect={entriesAllowedHorses}
                />
            ) : null}
        </SelectionOutlinedBox>
    );
}

export default SelectEntriesSection;
